import { EventSeries } from '../types/eventSeries.types';
import { formatDateTime } from './dateFormatter';

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Calcule la VRAIE dernière occurrence d'une série (pas la date de fin théorique)
 * Ex: Si l'event est tous les samedi/dimanche jusqu'au lundi 10 février,
 *     on retourne dimanche 8 février (la vraie dernière occurrence)
 */
export const computeSeriesEndDate = (series: EventSeries): Date | undefined => {
  if (!series.startDate || !series.startTime) return undefined;

  const dayMap: Record<string, number> = {
    'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
    'thursday': 4, 'friday': 5, 'saturday': 6
  };

  const startDate = new Date(series.startDate + 'T' + series.startTime);
  let lastOccurrence: Date | undefined;

  if (series.frequency === 'daily') {
    let currentDate = new Date(startDate);
    const theoreticalEndDate = series.endType === 'date' && series.endDate
      ? new Date(series.endDate + 'T' + series.startTime)
      : null;

    let count = 0;
    const maxOccurrences = series.endType === 'date' ? 365 : (series.occurrences ?? 365);

    while (
      (theoreticalEndDate && currentDate <= theoreticalEndDate) ||
      (!theoreticalEndDate && series.occurrences && count < series.occurrences)
    ) {
      if (count >= maxOccurrences) break;
      lastOccurrence = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
      count++;
    }
  } else if (series.frequency === 'weekly' && series.weekDays?.length) {
    const weekDayIndices = series.weekDays
      .map(day => dayMap[day.toLowerCase()] ?? -1)
      .filter(d => d >= 0);

    let currentDate = new Date(startDate);
    const theoreticalEndDate = series.endType === 'date' && series.endDate
      ? new Date(series.endDate + 'T' + series.startTime)
      : null;

    const totalOccurrences = series.occurrences && weekDayIndices.length > 0
      ? series.occurrences * weekDayIndices.length
      : series.occurrences;

    // Fallback: si pas de endDate ni d'occurrences, on ne peut pas calculer ici
    // Le caller (useSmartSeries/useSeriesBatch) utilisera les occurrences réelles
    if (!theoreticalEndDate && !totalOccurrences) {
      return undefined;
    }

    let count = 0;
    const maxIterations = 365 * 2;
    let iterations = 0;

    while (
      ((theoreticalEndDate && currentDate <= theoreticalEndDate) ||
      (!theoreticalEndDate && totalOccurrences && count < totalOccurrences)) &&
      iterations < maxIterations
    ) {
      if (weekDayIndices.includes(currentDate.getDay())) {
        lastOccurrence = new Date(currentDate);
        count++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
      iterations++;
    }
  }

  return lastOccurrence;
};

/**
 * Formate le pattern de récurrence d'une série
 * Ex: "Every Monday and Wednesday at 10:00 until Feb 14"
 */
export const computeSeriesPattern = (series: EventSeries, endDate: Date | undefined): string | null => {
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  let formattedPattern = '';

  if (series.frequency === 'daily') {
    formattedPattern = 'Every day';
  } else if (series.frequency === 'weekly' && series.weekDays && series.weekDays.length > 0) {
    const orderedDays = DAYS_ORDER.map(capitalizeFirstLetter);
    const selectedDays = series.weekDays
      .map(day => day.toLowerCase())
      .sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b))
      .map(capitalizeFirstLetter);

    if (selectedDays.length === 1) {
      formattedPattern = `Every ${selectedDays[0]}`;
    } else if (selectedDays.length === 7) {
      formattedPattern = 'Every day';
    } else if (selectedDays.length >= 5) {
      const exceptDays = orderedDays.filter(day => !selectedDays.includes(day));
      formattedPattern = `Every day <strong>except</strong> ${exceptDays.join(' and ')}`;
    } else {
      const lastDay = selectedDays[selectedDays.length - 1];
      const otherDays = selectedDays.slice(0, -1);
      formattedPattern = `Every ${otherDays.join(', ')} and ${lastDay}`;
    }
  }

  // Handle time display: variable schedules vs uniform
  if (series.daySchedules && Object.keys(series.daySchedules).length > 0) {
    // Group days by their schedule (e.g., Mon+Fri at 07:30, Wed at 08:00)
    const groups = groupDaysBySchedule(series.daySchedules);
    if (groups.length > 0) {
      const timesPart = groups.map(g => {
        const daysStr = g.days.map(d => d.slice(0, 3)).map(capitalizeFirstLetter).join(', ');
        const time = `${formatTime(g.start)}${g.end ? ` - ${formatTime(g.end)}` : ''}`;
        return `${daysStr} ${time}`;
      }).join(' · ');
      formattedPattern += ` (${timesPart})`;
    }
  } else if (series.startTime) {
    formattedPattern += ` at ${formatTime(series.startTime)}`;
    if (series.endTime) {
      formattedPattern += ` - ${formatTime(series.endTime)}`;
    }
  }

  if (endDate) {
    formattedPattern += ` until ${formatDateTime(endDate).date}`;
  }

  return formattedPattern || null;
};

/**
 * Groups days that share the same start/end time
 * e.g., { monday: {start:"07:30", end:"09:00"}, wednesday: {start:"08:00", end:"09:30"}, friday: {start:"07:30", end:"09:00"} }
 * → [{ days: ["monday","friday"], start: "07:30", end: "09:00" }, { days: ["wednesday"], start: "08:00", end: "09:30" }]
 */
export function groupDaysBySchedule(
  daySchedules: Record<string, { start: string; end: string }>
): Array<{ days: string[]; start: string; end: string }> {
  const groups: Map<string, { days: string[]; start: string; end: string }> = new Map();

  for (const day of DAYS_ORDER) {
    const schedule = daySchedules[day];
    if (!schedule) continue;
    const key = `${schedule.start}|${schedule.end || ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.days.push(day);
    } else {
      groups.set(key, { days: [day], start: schedule.start, end: schedule.end });
    }
  }

  return Array.from(groups.values());
}
